import {
  AfterViewInit,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ShamSlidesSwiper } from 'sham-components/elements/slides-swiper';
import { SwiperSlide, SwiperSlideLocale } from 'sham-components/shared/models';
import { SiteHomeBannerItem } from 'sham-components/shared/models/store-content';
import { WebsiteContext } from '@/app/services/website-context';
import { HomeCardsList } from '@/app/elements/home-cards-list/home-cards-list';
import { HomePopularCategories } from './home-popular-categories/home-popular-categories';
import { HomeRecommendedProducts } from './home-recommended-products/home-recommended-products';
import {
  ShamDeliveryTruck01StrokeIcon,
  ShamHeadphones1Icon,
  ShamPackageIcon,
  ShamShoppingBagIcon,
} from 'sham-components/icons';
import { HomeSimpleAds } from './home-banners-ads/home-banners-ads';
import { WideBanner } from 'sham-components/elements/home-banner-ads';
import { HomeSalesAds } from './home-sales-ads/home-sales-ads';
import { HomeFeaturedProducts } from './home-featured-products/home-featured-products';
import { HomeNewProducts } from './home-new-products/home-new-products';
import { HomeGreatDeals } from './home-great-deals/home-great-deals';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from 'sham-components/services';
import { NgComponentOutlet } from '@angular/common';
import { HomeNews } from './home-news/home-news';

@Component({
  selector: 'app-home-page',
  imports: [
    ShamSlidesSwiper,
    HomeCardsList,
    HomePopularCategories,
    HomeRecommendedProducts,
    HomeSimpleAds,
    HomeSalesAds,
    WideBanner,
    HomeFeaturedProducts,
    HomeNewProducts,
    HomeGreatDeals,
    HomeNews,
    TranslateModule,
    NgComponentOutlet,
  ],
  templateUrl: './home-page.html',
  styleUrl: './home-page.css',
})
export class HomePage implements AfterViewInit {
  private readonly ctx = inject(WebsiteContext);
  private lang = inject(LanguageService);
  readonly home = this.ctx.home;
  readonly news = computed(() => this.home()?.news ?? []);

  slides = signal<SwiperSlide[]>([]);
  banners = signal<SiteHomeBannerItem[]>([]);
  salesBanners = signal<SiteHomeBannerItem[]>([]);
  bannerImage = signal<string | null>(null);
  bannerLink = signal<string | null>(null);

  private el = inject(ElementRef);

  readonly features = [
    {
      title: 'Free Shipping',
      description: '30 Free Shipping',
      icon: ShamDeliveryTruck01StrokeIcon,
    },
    {
      title: 'Great Support 24/7',
      description: 'Instant access to Contact',
      icon: ShamHeadphones1Icon,
    },
    {
      title: '100% Secure',
      description: 'We ensure your money',
      icon: ShamShoppingBagIcon,
      highlighted: true,
    },
    {
      title: 'Safe Payment',
      description: '30 days money-back',
      icon: ShamPackageIcon,
    },
  ];

  ngOnInit() {
    this.loadHomeContent();
  }

  ngAfterViewInit() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const observeNewElements = () => {
      const sections = this.el.nativeElement.querySelectorAll(
        '.reveal:not(.observed)',
      );
      sections.forEach((section: HTMLElement) => {
        section.classList.add('observed');
        observer.observe(section);
      });
    };

    observeNewElements();

    const mutationObserver = new MutationObserver(() => {
      observeNewElements();
    });

    mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private async loadHomeContent() {
    try {
      const lang = this.lang.currentLang(); // 👈 مهم

      await this.ctx.loadHome(lang);
      const home = this.ctx.home()!;
      if (!home) return;

      const categories = await this.ctx.loadCategoriesTree(lang);

      // Map main banners to swiper slides
      const swipeSlides: SwiperSlide[] = home.mainBanners.map((banner, idx) => ({
        id: `main-banner-${idx}`,
        order: idx + 1,
        imageUrl: banner.imageUrl!,
        locales: [
          {
            localeCode: 'en',
            title: banner.title1 || '',
            subtitle: [
              banner.title3Part1 || '',
              banner.title3Part2 || '',
              banner.title3Part3 || '',
            ],
            topText: banner.title2 || '',
            bottomText: banner.title4 || '',
          },
          {
            localeCode: 'ar',
            title: banner.title1 || '',
            subtitle: [
              banner.title3Part1 || '',
              banner.title3Part2 || '',
              banner.title3Part3 || '',
            ],
            topText: banner.title2 || '',
            bottomText: banner.title4 || '',
          },
        ] as SwiperSlideLocale[],
        categoryId: banner.categoryId || '',
        categoryLink: banner.link || '',
        categoryName: banner.title1 || '',
      }));

      this.slides.set(
        swipeSlides.length > 0
          ? swipeSlides.map((x) => {
              const cat = categories.find(
                (y) =>
                  y.slug === x.categoryLink ||
                  y.children?.find(
                    (y) =>
                      y.slug === x.categoryLink ||
                      y.children?.find((y) => y.slug === x.categoryLink),
                  ),
              )!;
              const cat2 = cat?.children?.find(
                (y) =>
                  y.slug === x.categoryLink ||
                  y.children?.find((y) => y.slug === x.categoryLink),
              );
              const cat3 = cat2?.children?.find((y) => y.slug === x.categoryLink);
              return {
                ...x,
                categoryLink: `${cat?.slug}${cat2 ? `/${cat2.slug}` : ''}${cat3 ? `/${cat3.slug}` : ''}`,
              };
            })
          : [],
      );

      // Set banners for home-banners-ads (second banners)
      this.banners.set(
        home.secondBanners?.map((x) => {
          const cat = categories.find(
            (y) =>
              y.slug === x.link ||
              y.children?.find(
                (y) =>
                  y.slug === x.link || y.children?.find((y) => y.slug === x.link),
              ),
          )!;
          const cat2 = cat?.children?.find(
            (y) => y.slug === x.link || y.children?.find((y) => y.slug === x.link),
          );
          const cat3 = cat2?.children?.find((y) => y.slug === x.link);
          return {
            ...x,
            link: `${cat?.slug}${cat2 ? `/${cat2.slug}` : ''}${cat3 ? `/${cat3.slug}` : ''}`,
          };
        }),
      );

      // Set banners for home-sales-ads (third banners)
      this.salesBanners.set(
        home.thirdBanners?.map((x) => {
          const cat = categories.find(
            (y) =>
              y.slug === x.link ||
              y.children?.find(
                (y) =>
                  y.slug === x.link || y.children?.find((y) => y.slug === x.link),
              ),
          )!;
          const cat2 = cat?.children?.find(
            (y) => y.slug === x.link || y.children?.find((y) => y.slug === x.link),
          );
          const cat3 = cat2?.children?.find((y) => y.slug === x.link);
          return {
            ...x,
            link: `${cat?.slug}${cat2 ? `/${cat2.slug}` : ''}${cat3 ? `/${cat3.slug}` : ''}`,
          };
        }),
      );
      console.log(home);

      // Set banner image from fourth banners if available
      if (home.forthBanners?.length > 0) {
        const banner = home.forthBanners[0];

        this.bannerImage.set(banner.imageUrl || null);

        const banLnk = banner.link;

        if (!banLnk) {
          this.bannerLink.set(null);
          return;
        }

        const banCat = categories.find(
          (y) =>
            y.slug === banLnk ||
            y.children?.some(
              (y) => y.slug === banLnk || y.children?.some((y) => y.slug === banLnk),
            ),
        );

        const banCat2 = banCat?.children?.find(
          (y) => y.slug === banLnk || y.children?.some((y) => y.slug === banLnk),
        );

        const banCat3 = banCat2?.children?.find((y) => y.slug === banLnk);

        const fullPath = [banCat?.slug, banCat2?.slug, banCat3?.slug]
          .filter(Boolean)
          .join('/');

        this.bannerLink.set(fullPath || null);
      }
    } catch (error) {
      console.error('Failed to load home content:', error);
      // Use fallback data
      this.slides.set([]);
    }
  }
}
